/* @flow */

import * as React from 'react';

import classNames from 'classnames';
import createUseThemeStyles from 'src/shared/app/base/util/createUseThemeStyles';
import styles from './Feed.style';

import InfiniteScroll from 'src/shared/app/base/component/infiniteScoll/InfiniteScroll';
import FeedQueryPage from 'src/shared/ui/component/data-display/FeedQueryPage';

import type {
  UseQuery,
  ApiListResult,
} from 'src/shared/app/base/api/type/apiQueryType';
import { defaultLimitPages } from 'src/shared/app/base/api/constant/apiConstant';
import { VARIANT } from 'src/shared/ui/component/button/Button';

type Props<T> = {
  useQuery: UseQuery<ApiListResult<T>>,
  renderSkeleton: () => React.Node,
  renderItem: (T) => React.Node,
  layoutClass?: string,
  emptyComponent?: React.Node,
  params?: Object,
  isCompact?: boolean,
  manualConfig?: { isActive: boolean, variant?: $Values<typeof VARIANT> },
  interstitialComponents?: Array<React.Node>,
  interstitialIndexes?: Array<number>,
};

const useStyles = createUseThemeStyles(styles);

function FeedQuery<T>({
  useQuery,
  renderSkeleton,
  renderItem,
  layoutClass,
  emptyComponent,
  params = {},
  isCompact,
  manualConfig = { isActive: false },
  interstitialComponents,
  interstitialIndexes,
}: Props<T>): React.Node {
  const classes = useStyles({ isCompact });
  const [currentPage, setCurrentPage] = React.useState(1);

  const currentLimit = params.limit || defaultLimitPages;

  const { data, isFetching } = useQuery({
    ...params,
    page: currentPage,
  });

  const count = data?.count || 0;

  const handleNext = () => {
    if (data && data.count > currentPage * currentLimit) {
      setCurrentPage((o) => o + 1);
    }
  };

  const itemsLeftCount = data ? data.count - currentPage * currentLimit : 0;
  const itemsLeft = itemsLeftCount <= 0 ? null : itemsLeftCount;

  if (data && !count && emptyComponent) {
    return emptyComponent;
  }

  const canQueryMore = count !== 0 && itemsLeft;

  return (
    <InfiniteScroll
      onNext={canQueryMore ? handleNext : undefined}
      manualConfig={{
        ...manualConfig,
        isActive: canQueryMore && manualConfig.isActive,
      }}
      itemsLeft={itemsLeft}
      isLazyFetching={isFetching}
    >
      <ul
        data-testid="feed-list"
        className={classNames(classes.feed, layoutClass)}
      >
        {Array.from(Array(currentPage).keys()).map((_, pageIndex) => (
          <FeedQueryPage
            useQuery={useQuery}
            renderItem={renderItem}
            params={params}
            page={pageIndex + 1}
            isCompact={isCompact}
            key={pageIndex}
            interstitialComponents={interstitialComponents}
            interstitialIndexes={interstitialIndexes}
          />
        ))}
        {isFetching && (
          <>
            <li className={classes.feedItem}>{renderSkeleton()}</li>
            <li className={classes.feedItem}>{renderSkeleton()}</li>
          </>
        )}
      </ul>
    </InfiniteScroll>
  );
}

export default FeedQuery;
